import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Navigate } from 'react-router';
import { PUBLIC_REGISTRATION_STATE_LABELS, PublicRegistrationState, WEB_ROUTES } from '@lsc/shared';
import { LoadingState } from '../../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { PageHeading } from '../components/PageHeading';
import { VisuallyHidden } from '../components/VisuallyHidden';
import { REGISTRATION_STATE_VIEWS } from '../error-states';
import { useRegistrationStatus } from '../hooks';
import { PendingView } from './PendingView';

/**
 * `/status` – pending page (§51) and provisioning wait page (§52, §53). Success is only shown
 * once the server reports ACTIVE.
 */
export function StatusPage() {
  const { query, timedOut, restartPolling } = useRegistrationStatus();

  if (query.isPending) return <LoadingState label="Status wird geladen …" />;
  if (query.isError) return <ErrorState error={query.error} onRetry={restartPolling} />;

  const { registration } = query.data;
  const { state } = registration;
  if (state === PublicRegistrationState.ACTIVE) {
    return <Navigate to={WEB_ROUTES.success} replace />;
  }

  const delayed =
    state === PublicRegistrationState.PROVISIONING_DELAYED ||
    (state === PublicRegistrationState.PROVISIONING && timedOut);

  return (
    <>
      <VisuallyHidden live>
        {delayed
          ? PUBLIC_REGISTRATION_STATE_LABELS.PROVISIONING_DELAYED
          : PUBLIC_REGISTRATION_STATE_LABELS[state]}
      </VisuallyHidden>
      {state === PublicRegistrationState.PENDING_EMAIL ? (
        <PendingView
          key={state}
          registration={registration}
          timedOut={timedOut}
          onRefresh={restartPolling}
        />
      ) : null}
      {state === PublicRegistrationState.PROVISIONING && !delayed ? (
        <ProvisioningView key={state} />
      ) : null}
      {delayed ? (
        <DelayedView key="delayed" timedOut={timedOut} onRefresh={restartPolling} />
      ) : null}
      {state === PublicRegistrationState.EXPIRED || state === PublicRegistrationState.CANCELLED ? (
        <ErrorState key={state} view={REGISTRATION_STATE_VIEWS[state]} />
      ) : null}
    </>
  );
}

function ProvisioningView() {
  return (
    <Stack spacing={3}>
      <PageHeading>Ihr Zugang wird gerade eingerichtet.</PageHeading>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress aria-hidden="true" size={32} />
        <Typography>Vielen Dank, Ihre E-Mail-Adresse ist bestätigt.</Typography>
      </Box>
      <Typography color="text.secondary">
        Das dauert in der Regel nur wenige Sekunden. Sie können diese Seite geöffnet lassen – sie
        aktualisiert sich automatisch.
      </Typography>
    </Stack>
  );
}

function DelayedView({ timedOut, onRefresh }: { timedOut: boolean; onRefresh: () => void }) {
  return (
    <Stack spacing={3}>
      <PageHeading>Die Einrichtung verzögert sich</PageHeading>
      <Typography>
        Ihre E-Mail-Adresse ist bestätigt und Ihr Platz ist für Sie reserviert. Die Einrichtung
        Ihres Zugangs dauert leider länger als üblich.
      </Typography>
      <Alert severity="info" role="note">
        Sie müssen nichts weiter tun. Sie erhalten eine E-Mail, sobald Ihr Zugang bereit ist. Sie
        können diese Seite schließen.
      </Alert>
      {timedOut ? (
        <Button variant="outlined" size="large" onClick={onRefresh}>
          Status erneut prüfen
        </Button>
      ) : null}
    </Stack>
  );
}
